import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../queries";
import { toast } from "react-toastify";
import { 
  getMedicalTestsApi, 
  createMedicalTestApi, 
  updateMedicalTestApi, 
  deleteMedicalTestApi,
  getMedicalTestCategoriesWithCountsApi,
  createCategoryApi,
  deleteCategoryApi
} from "../services/apiService";
import editIcon from "../assets/images/edit-icon.svg";
import deleteIcon from "../assets/images/delete-icon.svg";
import cancelIcon from "../assets/images/cancel-btn.svg";
import { formatDateInDayDateYear } from "../utils/commonData";

const TestManagement = () => {
  const queryClient = useQueryClient();
  const [testList, setTestList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [availableCategories, setAvailableCategories] = useState([]);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showDeleteTestPopup, setShowDeleteTestPopup] = useState(false);
  const [showDeleteCategoryPopup, setShowDeleteCategoryPopup] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoriesView, setShowCategoriesView] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: "", abrv: "" });
  const [addTestNameObj, setAddTestNameObj] = useState({
    id: "",
    name: "",
    abrv: "",
    category_name: "",
    category_id: "",
  });
  const [addTestBtnDis, setAddTestBtnDis] = useState(false);
  const [updateTestBtnDis, setUpdateTestBtnDis] = useState(false);
  const [deleteTestBtnDis, setDeleteTestBtnDis] = useState(false);
  const [deleteCategoryBtnDis, setDeleteCategoryBtnDis] = useState(false);
  const [addCategoryBtnDis, setAddCategoryBtnDis] = useState(false);
  const [forceDeleteCategory, setForceDeleteCategory] = useState(false);
  const [screenLoading, setScreenLoading] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTests, setTotalTests] = useState(0);
  const [perPage, setPerPage] = useState(15);

  const categoryTotalPages = Math.max(1, Math.ceil(availableCategories.length / perPage));
  const activeTotalPages = showCategoriesView ? categoryTotalPages : totalPages;
  const activeTotalRecords = showCategoriesView ? availableCategories.length : totalTests;
  const activeRecordLabel = showCategoriesView ? "categories" : "tests";
  const paginatedCategories = availableCategories.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const loadCategories = async () => {
    try {
      const response = await getMedicalTestCategoriesWithCountsApi();
      
      if (response?.data?.success && response.data.data) {
        const categories = response.data.data.map(cat => ({
          id: cat.id,
          name: cat.name,
          abrv: cat.abrv || cat.abbreviation || "",
          count: cat.medical_tests_count || 0,
          is_system_defined: cat.is_system_defined,
          created_at: cat.created_at,
        }));
        setAvailableCategories(categories);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error("Failed to load categories", { autoClose: 1500 });
    }
  };

  const getTestList = async (page = 1) => {
    try {
      setScreenLoading(true);
      const params = {
        page: page,
        per_page: perPage
      };
      
      if (searchTerm) params.search = searchTerm;
      if (categoryFilter) params.category_id = categoryFilter;
      
      const response = await getMedicalTestsApi(params);
      if (response?.data?.success) {
        const paginatedData = response.data.data;
        const tests = paginatedData?.data || [];
        
        setTestList(tests);
        setCurrentPage(paginatedData.current_page || 1);
        setTotalPages(paginatedData.last_page || 1);
        setTotalTests(paginatedData.total || 0);
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
    } finally {
      setScreenLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
  };
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= activeTotalPages) {
      setCurrentPage(newPage);
      if (!showCategoriesView) getTestList(newPage);
    }
  };

  const handleViewToggle = () => {
    setShowCategoriesView(current => !current);
    setCurrentPage(1);
  };

  const handleAddTestName = async () => {
    if (!addTestNameObj?.name) {
      toast.warning("Please add test name.", { autoClose: 1500 });
      return;
    }
    if (!addTestNameObj?.category_id) {
      toast.warning("Please select a category.", { autoClose: 1500 });
      return;
    }
    
    try {
      setAddTestBtnDis(true);
      const payload = {
        name: addTestNameObj.name,
        abrv: addTestNameObj.abrv || "",
        category_id: addTestNameObj.category_id,
        is_system_defined: false
      };
      
      const response = await createMedicalTestApi(payload);
      if (response?.data?.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.categoriesWithTests() });
        toast.success("Test added successfully.", { autoClose: 1500 });
        setAddTestNameObj({ id: "", name: "", abrv: "", category_name: "", category_id: "" });
        setShowTestModal(false);
        getTestList(currentPage);
        loadCategories();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Something went wrong. Please try again.", { autoClose: 1500 });
    } finally {
      setAddTestBtnDis(false);
    }
  };

  const handleUpdateTestName = async () => {
    if (!addTestNameObj?.id) {
      toast.warning("Test not found.", { autoClose: 1500 });
      return;
    }
    
    try {
      setUpdateTestBtnDis(true);
      const payload = {
        name: addTestNameObj.name,
        abrv: addTestNameObj.abrv || ""
      };
      
      if (addTestNameObj.category_id) {
        payload.category_id = addTestNameObj.category_id;
      }
      
      const response = await updateMedicalTestApi(addTestNameObj.id, payload);
      if (response?.data?.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.categoriesWithTests() });
        toast.success("Test updated successfully.", { autoClose: 1500 });
        setAddTestNameObj({ id: "", name: "", abrv: "", category_name: "", category_id: "" });
        setShowTestModal(false);
        getTestList(currentPage);
        loadCategories();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Something went wrong. Please try again.", { autoClose: 1500 });
    } finally {
      setUpdateTestBtnDis(false);
    }
  };

  const handleDeleteTestName = async () => {
    if (!selectedTest?.id) {
      toast.warning("Test not found.", { autoClose: 1500 });
      return;
    }
    
    try {
      setDeleteTestBtnDis(true);
      const response = await deleteMedicalTestApi(selectedTest.id);
      if (response?.data?.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.categoriesWithTests() });
        toast.success("Test removed successfully.", { autoClose: 1500 });
        setSelectedTest(null);
        setShowDeleteTestPopup(false);
        getTestList(currentPage);
        loadCategories();
      }
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(errorMsg, { autoClose: 1500 });
    } finally {
      setDeleteTestBtnDis(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory?.id) {
      toast.warning("Category not found.", { autoClose: 1500 });
      return;
    }
    
    if (selectedCategory.count > 0 && !forceDeleteCategory) {
      toast.warning("Please confirm that you want to delete this category along with all its tests.", { autoClose: 2000 });
      return;
    }
    
    if (selectedCategory.is_system_defined) {
      toast.error("Cannot delete system-defined categories.", { autoClose: 1500 });
      return;
    }
    
    try {
      setDeleteCategoryBtnDis(true);
      const response = await deleteCategoryApi(selectedCategory.id);
      if (response?.data?.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.categoriesWithTests() });
        const successMsg = selectedCategory.count > 0 
          ? `Category and ${selectedCategory.count} test${selectedCategory.count !== 1 ? 's' : ''} removed successfully.`
          : "Category removed successfully.";
        toast.success(successMsg, { autoClose: 2000 });
        setSelectedCategory(null);
        setShowDeleteCategoryPopup(false);
        setForceDeleteCategory(false);
        
        // If the deleted category was selected in filter, clear it
        if (categoryFilter === selectedCategory.id.toString()) {
          setCategoryFilter("");
        }
        
        loadCategories();
        getTestList(currentPage);
      }
    } catch (error) {
      console.error(error);
      const errorMsg = error.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(errorMsg, { autoClose: 3000 });
    } finally {
      setDeleteCategoryBtnDis(false);
    }
  };

  const handleAddCategory = async () => {
    const name = categoryForm.name.trim();
    const abrv = categoryForm.abrv.trim();

    if (!name) {
      toast.warning("Please add category name.", { autoClose: 1500 });
      return;
    }
    if (!abrv) {
      toast.warning("Please add category abbreviation.", { autoClose: 1500 });
      return;
    }

    try {
      setAddCategoryBtnDis(true);
      const response = await createCategoryApi({
        name,
        abrv,
        is_system_defined: false,
      });

      if (response?.data?.success) {
        queryClient.invalidateQueries({ queryKey: queryKeys.prescriptions.categoriesWithTests() });
        toast.success("Category added successfully.", { autoClose: 1500 });
        setCategoryForm({ name: "", abrv: "" });
        setShowCategoryModal(false);
        setCurrentPage(1);
        await loadCategories();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Something went wrong. Please try again.", { autoClose: 1500 });
    } finally {
      setAddCategoryBtnDis(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await loadCategories();
      getTestList(1);
    };
    initializeData();
  }, []);
  
  useEffect(() => {
    setCurrentPage(1);
    const timeoutId = setTimeout(() => {
      getTestList(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, categoryFilter]);

  return (
    <>
      <div className="configuration_prescription_management mainProtocolSection">
        <h3>
          Test Management
          <span style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="Search tests..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                fontSize: "14px",
                width: "180px"
              }}
            />
            <select
              value={categoryFilter}
              onChange={(e) => handleCategoryFilterChange(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                fontSize: "14px",
                width: "180px",
                cursor: "pointer",
                backgroundColor: "#fff"
              }}
            >
              <option value="">All Categories</option>
              {availableCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name} ({cat.count})
                </option>
              ))}
            </select>
            {(searchTerm || categoryFilter) && (
              <button
                onClick={clearFilters}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  cursor: "pointer",
                  transition: "all 0.3s",
                  whiteSpace: "nowrap"
                }}
                onMouseEnter={e => e.target.style.backgroundColor = "#f5f5f5"}
                onMouseLeave={e => e.target.style.backgroundColor = "#fff"}
              >
                Clear
              </button>
            )}
            <button
              onClick={handleViewToggle}
              style={{
                padding: "8px 16px",
                border: "1px solid #687eff",
                borderRadius: "6px",
                fontSize: "14px",
                backgroundColor: showCategoriesView ? "#687eff" : "#fff",
                color: showCategoriesView ? "#fff" : "#687eff",
                cursor: "pointer",
                transition: "all 0.3s",
                fontWeight: 500,
                whiteSpace: "nowrap"
              }}
            >
              {showCategoriesView ? "View Tests" : "Manage Categories"}
            </button>
            <button
              className="createNewBtn"
              onClick={() => {
                if (showCategoriesView) {
                  setCategoryForm({ name: "", abrv: "" });
                  setShowCategoryModal(true);
                } else {
                  setShowTestModal(true);
                  setSelectedTest(null);
                  setAddTestNameObj({ id: "", name: "", abrv: "", category_name: "", category_id: "" });
                }
              }}>
              {showCategoriesView ? "Add Category" : "Add Test"}
            </button>
          </span>
        </h3>
        <div className="upcomingAppoinment nurseDiv protocolSection">
          <div className="nurseList">
            <div className="appoList">
              {!showCategoriesView ? (
                <table>
                  <thead>
                    <tr>
                      <th>Test Name</th>
                      <th>Abbreviation</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Created on</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  {testList?.length > 0 ? (
                    <tbody>
                      {testList?.map((item, index) => (
                        <tr key={item.id || index}>
                          <td className="appoName">{item?.name}</td>
                          <td>{item?.abrv || 'N/A'}</td>
                          <td>{item?.category?.name || item?.categorie || 'N/A'}</td>
                          <td>
                            <span style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              backgroundColor: item?.is_system_defined ? "#e6f7ff" : "#f0f5ff",
                              color: item?.is_system_defined ? "#0958d9" : "#597ef7"
                            }}>
                              {item?.is_system_defined ? "System" : "Custom"}
                            </span>
                          </td>
                          <td>{formatDateInDayDateYear(item?.created_at)}</td>
                          <td>
                            <button
                              className="viewLink"
                              onClick={() => {
                                setSelectedTest(item);
                                setAddTestNameObj({
                                  id: item?.id,
                                  name: item?.name,
                                  abrv: item?.abrv || "",
                                  category_name: item?.category?.name || item?.categorie || "",
                                  category_id: item?.category_id || ""
                                });
                                setShowTestModal(true);
                              }}
                              disabled={item?.is_system_defined}
                              style={{ opacity: item?.is_system_defined ? 0.5 : 1 }}
                            >
                              <img src={editIcon} alt="editIcon" />
                            </button>{" "}
                            <button
                              className="viewLink"
                              onClick={() => {
                                setSelectedTest(item);
                                setShowDeleteTestPopup(true);
                              }}
                              disabled={item?.is_system_defined}
                              style={{ opacity: item?.is_system_defined ? 0.5 : 1 }}
                            >
                              <img src={deleteIcon} alt="deleteIcon" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan="6" className="centeredText">
                          {searchTerm || categoryFilter ? "No tests found matching your filters!" : "No Tests found!"}
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Category Name</th>
                      <th>Abbreviation</th>
                      <th>Test Count</th>
                      <th>Type</th>
                      <th>Created on</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  {availableCategories?.length > 0 ? (
                    <tbody>
                      {paginatedCategories.map((cat) => (
                        <tr key={cat.id}>
                          <td className="appoName">{cat.name}</td>
                          <td>{cat.abrv || "N/A"}</td>
                          <td>{cat.count} test{cat.count !== 1 ? 's' : ''}</td>
                          <td>
                            <span style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              backgroundColor: cat.is_system_defined ? "#e6f7ff" : "#f0f5ff",
                              color: cat.is_system_defined ? "#0958d9" : "#597ef7"
                            }}>
                              {cat.is_system_defined ? "System" : "Custom"}
                            </span>
                          </td>
                          <td>{formatDateInDayDateYear(cat.created_at)}</td>
                          <td>
                            <button
                              className="viewLink"
                              onClick={() => {
                                setSelectedCategory(cat);
                                setForceDeleteCategory(false);
                                setShowDeleteCategoryPopup(true);
                              }}
                              disabled={cat.is_system_defined}
                              style={{ opacity: cat.is_system_defined ? 0.5 : 1 }}
                              title={cat.is_system_defined ? "Cannot delete system categories" : cat.count > 0 ? `This category has ${cat.count} test(s)` : "Delete category"}
                            >
                              <img src={deleteIcon} alt="deleteIcon" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan="6" className="centeredText">
                          No Categories found!
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              )}
            </div>
            
            {/* Pagination Controls */}
            {activeTotalRecords > 0 && (
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px",
                borderTop: "1px solid #e8e8e8",
                flexWrap: "wrap",
                gap: "12px"
              }}>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, activeTotalRecords)} of {activeTotalRecords} {activeRecordLabel}
                </div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                      background: currentPage === 1 ? "#f5f5f5" : "#fff",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      color: currentPage === 1 ? "#bbb" : "#333"
                    }}
                  >
                    First
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                      background: currentPage === 1 ? "#f5f5f5" : "#fff",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      color: currentPage === 1 ? "#bbb" : "#333"
                    }}
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div style={{ display: "flex", gap: "4px" }}>
                    {(() => {
                      const pages = [];
                      const maxVisible = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                      let endPage = Math.min(activeTotalPages, startPage + maxVisible - 1);
                      
                      if (endPage - startPage < maxVisible - 1) {
                        startPage = Math.max(1, endPage - maxVisible + 1);
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #d9d9d9",
                              borderRadius: "6px",
                              background: currentPage === i ? "#687eff" : "#fff",
                              color: currentPage === i ? "#fff" : "#333",
                              cursor: "pointer",
                              fontSize: "14px",
                              fontWeight: currentPage === i ? 600 : 400,
                              minWidth: "36px"
                            }}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === activeTotalPages}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                      background: currentPage === activeTotalPages ? "#f5f5f5" : "#fff",
                      cursor: currentPage === activeTotalPages ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      color: currentPage === activeTotalPages ? "#bbb" : "#333"
                    }}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => handlePageChange(activeTotalPages)}
                    disabled={currentPage === activeTotalPages}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                      background: currentPage === activeTotalPages ? "#f5f5f5" : "#fff",
                      cursor: currentPage === activeTotalPages ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      color: currentPage === activeTotalPages ? "#bbb" : "#333"
                    }}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCategoryModal && (
        <div
          className="modal TemplateModal"
          onClick={() => setShowCategoryModal(false)}
        >
          <div
            className="modal-content"
            onClick={event => event.stopPropagation()}
            style={{
              borderRadius: "16px",
              boxShadow: "0 24px 60px rgba(30, 41, 59, 0.22)",
              maxWidth: "500px",
              overflow: "hidden",
            }}
          >
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #687eff 0%, #7c3aed 100%)",
                borderBottom: 0,
                padding: "24px",
              }}
            >
              <button
                type="button"
                className="CloseButton"
                aria-label="Close category form"
                onClick={() => setShowCategoryModal(false)}
              />
              <h3 style={{ color: "#fff", fontSize: "22px", margin: 0 }}>New category</h3>
              <p style={{ color: "rgba(255,255,255,0.82)", margin: "8px 0 0" }}>
                Create a reusable category for medical tests.
              </p>
            </div>
            <div className="modal-body" style={{ padding: "26px" }}>
              <div style={{ display: "grid", gap: "20px" }}>
                <div>
                  <label htmlFor="test-category-name" style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}>
                    Category name
                  </label>
                  <input
                    id="test-category-name"
                    className="form_control"
                    type="text"
                    placeholder="e.g., Hematology"
                    value={categoryForm.name}
                    onChange={event => setCategoryForm(current => ({ ...current, name: event.target.value }))}
                    style={{ border: "1px solid #d9d9e3", borderRadius: "9px", padding: "12px 14px", width: "100%" }}
                  />
                </div>
                <div>
                  <label htmlFor="test-category-abbreviation" style={{ display: "block", fontWeight: 600, marginBottom: "8px" }}>
                    Category abbreviation
                  </label>
                  <input
                    id="test-category-abbreviation"
                    className="form_control"
                    type="text"
                    placeholder="e.g., HEMA"
                    value={categoryForm.abrv}
                    onChange={event => setCategoryForm(current => ({ ...current, abrv: event.target.value.toUpperCase() }))}
                    style={{ border: "1px solid #d9d9e3", borderRadius: "9px", padding: "12px 14px", textTransform: "uppercase", width: "100%" }}
                  />
                </div>
              </div>
              <div style={{ borderTop: "1px solid #edf0f5", display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "26px", paddingTop: "20px" }}>
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  style={{ background: "#fff", border: "1px solid #d9d9e3", borderRadius: "9px", cursor: "pointer", padding: "11px 18px" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={addCategoryBtnDis}
                  onClick={handleAddCategory}
                  style={{
                    background: addCategoryBtnDis ? "#b7bef8" : "#687eff",
                    border: 0,
                    borderRadius: "9px",
                    color: "#fff",
                    cursor: addCategoryBtnDis ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    padding: "11px 20px",
                  }}
                >
                  {addCategoryBtnDis ? "Creating..." : "Create Category"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTestModal && (
        <div className="modal TemplateModal" onClick={() => setShowTestModal(false)}>
          <div
            className="modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="test-form-title"
            onClick={event => event.stopPropagation()}
            style={{
              borderRadius: "16px",
              boxShadow: "0 24px 70px rgba(30, 41, 59, 0.24)",
              maxWidth: "640px",
              overflow: "hidden",
              width: "92%",
            }}
          >
            <div
              className="modal-header"
              style={{
                background: "linear-gradient(135deg, #687eff 0%, #7c3aed 100%)",
                borderBottom: 0,
                padding: "26px 28px",
                position: "relative",
              }}
            >
              <button
                type="button"
                className="CloseButton"
                aria-label="Close test form"
                onClick={() => setShowTestModal(false)}
              />
              <h3 id="test-form-title" style={{ color: "#fff", fontSize: "23px", margin: 0 }}>
                {selectedTest ? "Edit medical test" : "New medical test"}
              </h3>
              <p style={{ color: "rgba(255,255,255,0.82)", fontSize: "14px", margin: "8px 0 0" }}>
                {selectedTest
                  ? "Update the test details and its assigned category."
                  : "Add a test and assign it to an existing category."}
              </p>
            </div>
            <div className="modal-body" style={{ background: "#f8fafc", padding: "28px" }}>
              <div
                className="white_form_box"
                style={{
                  background: "#fff",
                  border: "1px solid #e8ebf2",
                  borderRadius: "13px",
                  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
                  padding: "24px",
                }}
              >
                <div style={{ display: "grid", gap: "20px", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                  <div>
                    <label htmlFor="medical-test-name" style={{
                      color: "#273247",
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 500,
                      marginBottom: "8px",
                    }}>
                      Test Name <span style={{ color: "#ff4d4f" }}>*</span>
                    </label>
                    <input
                      id="medical-test-name"
                      aria-label="Test name"
                      type="text"
                      placeholder="Enter test name"
                      className="form_control"
                      value={addTestNameObj?.name || ""}
                      onChange={e => setAddTestNameObj(prev => ({ ...prev, name: e.target.value }))}
                      style={{
                        border: "1px solid #d9ddea",
                        borderRadius: "9px",
                        fontSize: "14px",
                        outline: "none",
                        padding: "12px 14px",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="medical-test-abbreviation" style={{
                      color: "#273247",
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 500,
                      marginBottom: "8px",
                    }}>
                      Abbreviation
                    </label>
                    <input
                      id="medical-test-abbreviation"
                      aria-label="Test abbreviation"
                      type="text"
                      placeholder="e.g., NFS, VS"
                      className="form_control"
                      value={addTestNameObj?.abrv || ""}
                      onChange={e => setAddTestNameObj(prev => ({ ...prev, abrv: e.target.value.toUpperCase() }))}
                      style={{
                        border: "1px solid #d9ddea",
                        borderRadius: "9px",
                        fontSize: "14px",
                        outline: "none",
                        padding: "12px 14px",
                        textTransform: "uppercase",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label htmlFor="medical-test-category" style={{
                      color: "#273247",
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 500,
                      marginBottom: "8px",
                    }}>
                      Category <span style={{ color: "#ff4d4f" }}>*</span>
                    </label>
                    <select
                      id="medical-test-category"
                      aria-label="Test category"
                      className="form_control"
                      value={addTestNameObj?.category_id || ""}
                      onChange={event => {
                        const category = availableCategories.find(item => String(item.id) === event.target.value);
                        setAddTestNameObj(previous => ({
                          ...previous,
                          category_id: event.target.value,
                          category_name: category?.name || "",
                        }));
                      }}
                      style={{
                        background: "#fff",
                        border: "1px solid #d9ddea",
                        borderRadius: "9px",
                        cursor: "pointer",
                        fontSize: "14px",
                        outline: "none",
                        padding: "12px 14px",
                        width: "100%",
                      }}
                    >
                      <option value="">Select an existing category</option>
                      {availableCategories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}{category.abrv ? ` (${category.abrv})` : ""}
                        </option>
                      ))}
                    </select>
                    <p style={{ color: "#7a8499", fontSize: "12px", margin: "7px 0 0" }}>
                      Categories are managed from the Manage Categories view.
                    </p>
                  </div>
                </div>
                <div style={{
                  borderTop: "1px solid #edf0f5",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                  marginTop: "26px",
                  paddingTop: "20px",
                }}>
                  <button
                    type="button"
                    onClick={() => setShowTestModal(false)}
                    style={{
                      background: "#fff",
                      border: "1px solid #d9ddea",
                      borderRadius: "9px",
                      color: "#4c566a",
                      cursor: "pointer",
                      padding: "11px 18px",
                    }}
                  >
                    Cancel
                  </button>
                  {selectedTest ? (
                    <button
                      type="button"
                      className="saveBtn"
                      disabled={updateTestBtnDis}
                      onClick={handleUpdateTestName}
                      style={{
                        background: updateTestBtnDis ? "#b7bef8" : "#687eff",
                        border: "none",
                        borderRadius: "9px",
                        color: "#fff",
                        cursor: updateTestBtnDis ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        padding: "11px 20px",
                      }}
                    >
                      {updateTestBtnDis ? "Updating..." : "Update Test"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="saveBtn"
                      disabled={addTestBtnDis}
                      onClick={handleAddTestName}
                      style={{
                        background: addTestBtnDis ? "#b7bef8" : "#687eff",
                        border: "none",
                        borderRadius: "9px",
                        color: "#fff",
                        cursor: addTestBtnDis ? "not-allowed" : "pointer",
                        fontWeight: 600,
                        padding: "11px 20px",
                      }}
                    >
                      {addTestBtnDis ? "Creating..." : "Create Test"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteTestPopup && (
        <div className="popup holidayPopup">
          <div className="popup-content removePop">
            <button className="cancelBtn" onClick={() => setShowDeleteTestPopup(false)}>
              <img src={cancelIcon} alt="cancelIcon" />
            </button>
            <div className="addNursePopup">
              <h2 className="removeHdn">Are you sure you want to delete this test?</h2>
              <div className="removeNurse">
                <div className="nurseBtns">
                  <button className="yesBtn" onClick={() => handleDeleteTestName()} disabled={deleteTestBtnDis}>
                    {deleteTestBtnDis ? "Please wait..." : "Yes"}
                  </button>
                  <button className="noBtn" onClick={() => setShowDeleteTestPopup(false)}>
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteCategoryPopup && (
        <div 
          className="modal" 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            backdropFilter: "blur(2px)"
          }}
          onClick={() => {
            setShowDeleteCategoryPopup(false);
            setForceDeleteCategory(false);
          }}
        >
          <div 
            className="modal-content" 
            onClick={e => e.stopPropagation()} 
            style={{ 
              maxWidth: "500px",
              width: "90%",
              borderRadius: "16px",
              backgroundColor: "#fff",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              overflow: "hidden",
              animation: "modalSlideIn 0.3s ease-out"
            }}
          >
            {/* Header */}
            <div style={{
              background: selectedCategory?.count > 0 ? "linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              padding: "24px",
              position: "relative"
            }}>
              <button 
                onClick={() => {
                  setShowDeleteCategoryPopup(false);
                  setForceDeleteCategory(false);
                }}
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "16px",
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  color: "#fff",
                  transition: "all 0.3s"
                }}
                onMouseEnter={e => e.target.style.background = "rgba(255, 255, 255, 0.3)"}
                onMouseLeave={e => e.target.style.background = "rgba(255, 255, 255, 0.2)"}
              >
                ×
              </button>
              <div style={{ textAlign: "center" }}>
                <div style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                  fontSize: "32px"
                }}>
                  {selectedCategory?.count > 0 ? "⚠️" : "🗑️"}
                </div>
                <h2 style={{ 
                  margin: 0, 
                  fontSize: "24px", 
                  fontWeight: 700, 
                  color: "#fff",
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)"
                }}>
                  {selectedCategory?.count > 0 
                    ? "Delete Category with Tests?"
                    : "Delete Category?"}
                </h2>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: "32px 24px" }}>
              <div style={{
                backgroundColor: "#f8f9fa",
                padding: "16px",
                borderRadius: "12px",
                marginBottom: "24px",
                border: "1px solid #e9ecef"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                  <span style={{
                    fontSize: "20px",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fff",
                    borderRadius: "8px"
                  }}>📁</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "16px", fontWeight: 600, color: "#2d3748", marginBottom: "4px" }}>
                      {selectedCategory?.name}
                    </div>
                    <div style={{ fontSize: "14px", color: "#718096" }}>
                      {selectedCategory?.count} test{selectedCategory?.count !== 1 ? 's' : ''} in this category
                    </div>
                  </div>
                </div>
              </div>

              {selectedCategory?.count > 0 ? (
                <>
                  <div style={{
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffeaa7",
                    borderRadius: "12px",
                    padding: "16px",
                    marginBottom: "20px"
                  }}>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <span style={{ fontSize: "20px", flexShrink: 0 }}>⚠️</span>
                      <div>
                        <div style={{ fontSize: "14px", fontWeight: 600, color: "#856404", marginBottom: "8px" }}>
                          Warning: Category Contains Tests
                        </div>
                        <p style={{ margin: 0, fontSize: "14px", color: "#856404", lineHeight: "1.6" }}>
                          Deleting this category will <strong>permanently remove all {selectedCategory.count} test{selectedCategory.count !== 1 ? 's' : ''}</strong> within it. This action cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>

                  <label style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "16px",
                    backgroundColor: forceDeleteCategory ? "#e8f4fd" : "#f8f9fa",
                    border: forceDeleteCategory ? "2px solid #667eea" : "2px solid #e9ecef",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    marginBottom: "24px"
                  }}>
                    <input
                      type="checkbox"
                      checked={forceDeleteCategory}
                      onChange={(e) => setForceDeleteCategory(e.target.checked)}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        marginTop: "2px",
                        accentColor: "#667eea"
                      }}
                    />
                    <span style={{ 
                      fontSize: "14px", 
                      color: "#2d3748", 
                      lineHeight: "1.6",
                      userSelect: "none"
                    }}>
                      I understand that this will <strong>permanently delete the category and all {selectedCategory.count} test{selectedCategory.count !== 1 ? 's' : ''}</strong> inside it.
                    </span>
                  </label>
                </>
              ) : (
                <p style={{ 
                  margin: "0 0 24px 0", 
                  fontSize: "15px", 
                  color: "#4a5568", 
                  lineHeight: "1.6",
                  textAlign: "center" 
                }}>
                  Are you sure you want to delete the <strong>"{selectedCategory?.name}"</strong> category? This action cannot be undone.
                </p>
              )}

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                <button
                  onClick={() => {
                    setShowDeleteCategoryPopup(false);
                    setForceDeleteCategory(false);
                  }}
                  style={{
                    padding: "12px 24px",
                    border: "2px solid #e2e8f0",
                    borderRadius: "10px",
                    fontSize: "15px",
                    fontWeight: 600,
                    backgroundColor: "#fff",
                    color: "#4a5568",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    minWidth: "100px"
                  }}
                  onMouseEnter={e => {
                    e.target.style.backgroundColor = "#f7fafc";
                    e.target.style.borderColor = "#cbd5e0";
                  }}
                  onMouseLeave={e => {
                    e.target.style.backgroundColor = "#fff";
                    e.target.style.borderColor = "#e2e8f0";
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteCategory}
                  disabled={deleteCategoryBtnDis || (selectedCategory?.count > 0 && !forceDeleteCategory)}
                  style={{
                    padding: "12px 24px",
                    border: "none",
                    borderRadius: "10px",
                    fontSize: "15px",
                    fontWeight: 600,
                    backgroundColor: (deleteCategoryBtnDis || (selectedCategory?.count > 0 && !forceDeleteCategory)) ? "#cbd5e0" : "#ef4444",
                    color: "#fff",
                    cursor: (deleteCategoryBtnDis || (selectedCategory?.count > 0 && !forceDeleteCategory)) ? "not-allowed" : "pointer",
                    transition: "all 0.3s",
                    minWidth: "140px",
                    opacity: (deleteCategoryBtnDis || (selectedCategory?.count > 0 && !forceDeleteCategory)) ? 0.6 : 1
                  }}
                  onMouseEnter={e => {
                    if (!deleteCategoryBtnDis && !(selectedCategory?.count > 0 && !forceDeleteCategory)) {
                      e.target.style.backgroundColor = "#dc2626";
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.boxShadow = "0 4px 12px rgba(239, 68, 68, 0.4)";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!deleteCategoryBtnDis && !(selectedCategory?.count > 0 && !forceDeleteCategory)) {
                      e.target.style.backgroundColor = "#ef4444";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }
                  }}
                >
                  {deleteCategoryBtnDis ? (
                    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                      <span style={{ 
                        display: "inline-block",
                        width: "14px",
                        height: "14px",
                        border: "2px solid #fff",
                        borderTopColor: "transparent",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite"
                      }}></span>
                      Deleting...
                    </span>
                  ) : "Delete Category"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TestManagement;
